import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { getPainelProfessor } from '../services/dashboardService';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import AgendaProfessor from '../components/agenda/AgendaProfessor';
import {
  KpiCard, MonthlyBars, NotasDistribution, SituacaoPill, SectionCard,
  HabilidadesDistribution, ConceitosBar, ConceitosRows, ConceitosLegenda,
  cardCls, fmt, pct, rateColor, BRAND,
} from '../components/PainelDiarioUI';

/**
 * Meu Painel — cockpit do professor sobre a sua turma.
 *
 * Lê o diário que ele já preenche (/dashboard-diario/professor/) e devolve
 * "o que resolver hoje": pendências, alunos em risco, os números da turma e
 * a lista de alunos com frequência e média. Como o professor costuma ter uma
 * turma, o painel abre direto; com mais de uma, mostra um seletor.
 *
 * No 1º e 2º ano e na Educação Infantil não há nota: o diário guarda o
 * Registro de Habilidades (HND, HDI, HD, HC por habilidade e bimestre), e o
 * painel troca média e distribuição de notas pelos conceitos lançados.
 */
const NIVEL = {
  critico: { ico: '!', cls: 'bg-[#E83A3C]/10 text-[#E83A3C]' },
  atencao: { ico: '•', cls: 'bg-[#F9B047]/15 text-[#B87608]' },
  info: { ico: '✓', cls: 'bg-[#0974BA]/10 text-[#0974BA]' },
};

const ACAO_LABEL = { frequencia: 'Ver frequência', diario: 'Abrir diário', notas: 'Lançar notas' };

/** Contagens do aluno em texto curto: "3 HC · 1 HD". */
const resumoConceitos = (h) =>
  ['HC', 'HD', 'HDI', 'HND']
    .filter((c) => h?.[c]?.count)
    .map((c) => `${h[c].count} ${c}`)
    .join(' · ');

function MeuPainel() {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const [idClass, setIdClass] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Professor recém-cadastrado ainda não tem turma: é o estado normal de
  // quem acabou de receber o acesso, não uma falha. Guardado à parte do
  // `error` porque o texto e o tom são outros.
  const [semTurma, setSemTurma] = useState(null);

  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const strong = darkMode ? 'text-slate-100' : 'text-slate-800';

  const fetchPanel = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSemTurma(null);
    try {
      setData(await getPainelProfessor({ idClass: idClass || null }));
    } catch (err) {
      const corpo = err?.response?.data;
      if (corpo?.code === 'sem_turma') {
        setSemTurma(corpo);
      } else {
        console.error('Erro ao carregar Meu Painel:', err);
        setError('Não foi possível carregar o painel da turma.');
      }
    } finally {
      setLoading(false);
    }
  }, [idClass]);

  useEffect(() => { fetchPanel(); }, [fetchPanel]);

  if (loading) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Meu Painel..."
        subtitle="Reunindo frequência e notas da turma..."
        size="xl"
      />
    );
  }

  if (error) {
    return <div className="rounded-xl p-4 bg-[#E83A3C]/10 text-[#E83A3C] text-sm">{error}</div>;
  }

  if (semTurma) {
    return (
      <>
        <h1 className={`text-2xl font-medium mb-1 ${strong}`}>Meu Painel</h1>
        <div className={`mt-5 rounded-2xl p-6 max-w-2xl ${cardCls(darkMode)}`}>
          <div className="text-3xl mb-3" aria-hidden="true">📗</div>
          <h2 className={`text-lg font-medium mb-2 ${strong}`}>
            {semTurma.error || 'Você ainda não tem turma atribuída.'}
          </h2>
          <p className={`text-sm leading-relaxed ${muted}`}>{semTurma.detalhe}</p>
          <p className={`text-sm mt-4 ${muted}`}>
            Não há nada errado com o seu acesso — o cadastro do professor e a
            atribuição das turmas são duas etapas, e a segunda é feita pela
            coordenação.
          </p>
        </div>
      </>
    );
  }

  // Professor com mais de uma turma: seletor.
  if (data?.needs_selection) {
    const listaDeTurmas = (cls) => (
      <div className={cls}>
        {(data.classes || []).map(c => (
          <button key={c.id_class} onClick={() => setIdClass(String(c.id_class))}
            className={`text-left rounded-xl p-4 transition-all hover:border-[#0974BA] ${cardCls(darkMode)}`}>
            <div className={`font-medium ${strong}`}>{c.class_name}</div>
            <div className={`text-xs mt-0.5 ${muted}`}>
              {[c.school_name, c.grade, c.shift].filter(Boolean).join(' · ')}
            </div>
          </button>
        ))}
      </div>
    );
    return (
      <>
        <h1 className={`text-2xl font-medium mb-1 ${strong}`}>Meu Painel</h1>
        <p className={`text-sm mb-6 ${muted}`}>Suas aulas de hoje e o painel de cada turma.</p>
        <AgendaProfessor
          ladoDesktop={(
            <>
              <h2 className={`text-sm font-semibold mb-3 ${strong}`}>Escolha a turma para ver o painel</h2>
              {listaDeTurmas('grid grid-cols-2 gap-3')}
            </>
          )}
        />
        <div className="lg:hidden">
          <h2 className={`text-sm font-semibold mb-2 ${strong}`}>Escolha a turma para ver o painel</h2>
          {listaDeTurmas('grid grid-cols-1 sm:grid-cols-2 gap-3')}
        </div>
      </>
    );
  }

  if (!data?.turma) return null;
  const { turma, kpis, pendencias = [], notas, habilidades, alunos = [] } = data;
  const porHabilidades = Boolean(turma.serie_habilidades);

  const kpiCards = (
    <>
      <KpiCard label="Aulas registradas" value={fmt(kpis.aulas)} foot="no ano" tone="nav" darkMode={darkMode} />
      <KpiCard label="Frequência da turma" value={pct(kpis.frequencia)} foot="meta ≥ 75%"
        tone={kpis.frequencia >= 90 ? 'good' : kpis.frequencia >= 75 ? 'warn' : 'crit'} darkMode={darkMode} />
      {porHabilidades ? (
        <KpiCard label="Habilidades consolidadas"
          value={habilidades?.total ? pct(habilidades.HC?.pct) : '—'}
          foot={habilidades?.total ? `HC · ${fmt(habilidades.HD?.pct)}% em desenvolvimento (HD)` : 'nenhum conceito lançado'}
          tone="good" darkMode={darkMode} />
      ) : (
        <KpiCard label="Média da turma" value={kpis.media != null ? fmt(kpis.media) : '—'} foot="médias lançadas"
          tone={(kpis.media || 0) >= 7 ? 'good' : 'warn'} darkMode={darkMode} />
      )}
      <KpiCard label="Alunos em atenção" value={fmt(kpis.alunos_em_atencao)}
        foot={porHabilidades ? 'faltas ou metade em HND' : 'baixa nota ou faltas'}
        tone={kpis.alunos_em_atencao > 0 ? 'crit' : 'good'} darkMode={darkMode} />
    </>
  );

  return (
    <>
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-6">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'}`}>
            {turma.class_name}
          </h1>
          <p className={`text-sm mt-1 ${muted}`}>
            {[turma.school_name, turma.grade, turma.shift, `${turma.alunos_total} alunos`].filter(Boolean).join(' · ')}
          </p>
        </div>
        <button onClick={() => navigate(`/turmas/${turma.id_class}/diario`)}
          className="px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-all bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25">
          Abrir Diário da Turma
        </button>
      </header>

      {/* Agenda do dia: em que aula estou e qual é a próxima, de todas as turmas. */}
      <AgendaProfessor
        ladoDesktop={(
          <>
            <h2 className={`text-sm font-semibold mb-3 ${strong}`}>
              Números da turma <span className={`font-normal ${muted}`}>· {turma.class_name}</span>
            </h2>
            {/* Os cartões esticam até a altura da agenda; o número fica no meio. */}
            <div className="flex-1 grid grid-cols-2 auto-rows-fr gap-3 [&>*]:flex [&>*]:flex-col [&>*]:justify-center">
              {kpiCards}
            </div>
          </>
        )}
      />

      {/* Pendências — o que resolver hoje */}
      {pendencias.length > 0 && (
        <div className="mb-6">
          <h2 className={`text-sm font-semibold mb-2 ${strong}`}>
            Precisa da sua atenção <span className={`font-normal ${muted}`}>· o que resolver</span>
          </h2>
          <div className={`rounded-2xl divide-y ${darkMode ? 'divide-slate-700/60' : 'divide-slate-100'} ${cardCls(darkMode)}`}>
            {pendencias.map((p, i) => {
              const n = NIVEL[p.nivel] || NIVEL.info;
              return (
                <div key={i} className="flex items-center gap-3 p-4">
                  <div className={`w-8 h-8 rounded-lg grid place-items-center font-bold text-sm flex-none ${n.cls}`}>{n.ico}</div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium ${strong}`}>{p.titulo}</div>
                    {p.detalhe && <div className={`text-xs ${muted}`}>{p.detalhe}</div>}
                  </div>
                  {p.acao && (
                    <button onClick={() => navigate(`/turmas/${turma.id_class}/${p.acao === 'frequencia' ? 'frequencia' : 'diario'}`)}
                      className={`text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap ${darkMode ? 'bg-slate-700 text-slate-200 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                      {ACAO_LABEL[p.acao] || 'Abrir'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KPIs da turma — no desktop eles sobem para o lado da agenda. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 lg:hidden">
        {kpiCards}
      </div>

      {/* Notas (ou habilidades) + aulas por mês */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {porHabilidades ? (
          <SectionCard
            title="Habilidades da turma"
            subtitle={`${turma.serie_habilidades} · último conceito de cada aluno em cada habilidade`}
            darkMode={darkMode}
          >
            <HabilidadesDistribution habilidades={habilidades} darkMode={darkMode} />
          </SectionCard>
        ) : (
          <SectionCard title="Notas da turma" subtitle="médias lançadas · a recuperação já entra" darkMode={darkMode}>
            <NotasDistribution notas={notas} darkMode={darkMode} />
          </SectionCard>
        )}
        <SectionCard title="Minhas aulas por mês" subtitle="ritmo de registro no diário" darkMode={darkMode}>
          <MonthlyBars data={data.aulas_por_mes} darkMode={darkMode} />
        </SectionCard>
      </div>

      {/* Habilidades: evolução por bimestre e por componente */}
      {porHabilidades && habilidades?.total > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <SectionCard title="Evolução por bimestre" subtitle="conceitos lançados em cada bimestre" darkMode={darkMode}>
            <ConceitosRows
              darkMode={darkMode}
              rows={(habilidades.por_bimestre || []).map((b) => ({ label: `${b.bimestre}º bimestre`, contagem: b }))}
            />
            <ConceitosLegenda darkMode={darkMode} />
          </SectionCard>
          <SectionCard title="Por componente" subtitle="último conceito de cada habilidade" darkMode={darkMode}>
            <ConceitosRows
              darkMode={darkMode}
              rows={(habilidades.por_componente || []).map((c) => ({ label: c.componente, contagem: c }))}
            />
            <ConceitosLegenda darkMode={darkMode} />
          </SectionCard>
        </div>
      )}

      {/* Meus alunos */}
      <SectionCard
        title="Meus alunos"
        subtitle={`frequência e ${porHabilidades ? 'habilidades' : 'média'} — ordem alfabética · faltas justificadas contam nos 75%`}
        darkMode={darkMode}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-[11px] uppercase tracking-wide ${muted}`}>
                <th className="text-left font-semibold pb-2">Aluno</th>
                <th className="text-center font-semibold pb-2">Frequência</th>
                <th className="text-center font-semibold pb-2" title="Faltas justificadas contam nos 75%, mas aparecem à parte">
                  Faltas
                </th>
                <th className="text-right font-semibold pb-2">{porHabilidades ? 'Habilidades' : 'Média'}</th>
                <th className="text-right font-semibold pb-2">Situação</th>
              </tr>
            </thead>
            <tbody>
              {alunos.map((a) => (
                <tr key={a.id_student} className={darkMode ? 'border-t border-slate-700/60' : 'border-t border-slate-100'}>
                  <td className={`py-2.5 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{a.student_name}</td>
                  <td className={`py-2.5 text-center font-semibold tabular-nums ${rateColor(a.frequencia)}`}>{pct(a.frequencia)}</td>
                  <td className={`py-2.5 text-center tabular-nums ${muted}`}>
                    {a.faltas ?? 0}
                    {a.faltas_justificadas > 0 && (
                      <span
                        className="ml-1 text-[11px]"
                        title={`${a.faltas_justificadas} com justificativa — a lei não abona a falta, então continuam contando nos 75%`}
                      >
                        ({a.faltas_justificadas} just.)
                      </span>
                    )}
                  </td>
                  {porHabilidades ? (
                    <td className="py-2.5 pl-2 sm:pl-3">
                      {a.habilidades?.total ? (
                        <div className="flex flex-col items-end gap-1 ml-auto w-20 sm:w-auto">
                          <ConceitosBar contagem={a.habilidades} darkMode={darkMode} className="h-2.5 w-full sm:w-28" />
                          {/* No celular o resumo quebra em linhas, senão a tabela vaza da tela. */}
                          <span className={`text-[11px] text-right tabular-nums sm:whitespace-nowrap ${muted}`}>{resumoConceitos(a.habilidades)}</span>
                        </div>
                      ) : (
                        <div className={`text-right ${muted}`}>—</div>
                      )}
                    </td>
                  ) : (
                    <td className="py-2.5 text-right font-semibold tabular-nums" style={{ color: a.media == null ? undefined : a.media < 5 ? BRAND.crit : a.media < 7 ? BRAND.warn : BRAND.good }}>
                      {a.media != null ? fmt(a.media) : '—'}
                    </td>
                  )}
                  <td className="py-2.5 text-right"><SituacaoPill situacao={a.situacao} /></td>
                </tr>
              ))}
              {alunos.length === 0 && (
                <tr><td colSpan={5} className={`py-6 text-center ${muted}`}>Nenhum aluno cadastrado nesta turma.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <p className={`mt-8 pt-5 border-t text-sm ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
        É o seu próprio diário, resumido: nada de lançamento novo. O painel lê a frequência, as aulas e as
        {porHabilidades ? ' habilidades' : ' notas'} que você já registra e destaca <b className={strong}>o que precisa de atenção</b>.
      </p>
    </>
  );
}

export default MeuPainel;
